/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  TextField,
  Popover,
  Backdrop,
  CircularProgress,
  Toolbar,
  InputAdornment,
  Tooltip,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import Topheader from "../../../Layout/header/header";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "../../../Components/Table.module.css";
import { ExpandLess, ExpandMore, Search } from "@mui/icons-material";
import { fetchContentsDetails } from "../../../Services/redux/contents/ContentsDetailsActions";
import { fetchAdminCountriesDetails } from "../../../Services/redux/Oem-admin-counties/OemAdminCountryDetailsActions";
import "./oem-applicationList.css";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { DrawerFilter } from "../../../Components/ApplicationStatistics/Developer/DrawerFilter";
import { fetchOemAdminEcuDetails } from "../../../Services/redux/Ecu-types/OemAdminEcuActions";
import { usePortfolioChange } from "../../../hooks/usePortfolioChange";
import { getConfig } from "../../../config";
import { fetchOemAdminSelf } from "../../../Services/redux/oem-admin-applicationList/OemAdminApplicationListActions";

function NoRowsOverlay() {
  return (
    <Box
      height="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Typography variant="body2" color="textSecondary">
        No applications found.
      </Typography>
    </Box>
  );
}

export default function OEMApplicationList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [loader, setLoader] = useState(true); // Start with loader immediately
  const [invalidUserLogin, setInvalidUserLogin] = useState(false);
  const [invalidUserLoginMessage, setInvalidUserLoginMessage] = useState("");
  const [pendingApprovalUser, setPendingApprovalUser] = useState(false);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [isCountNeeded, setIsCountNeeded] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popupContent, setPopupContent] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRows, setFilteredRows] = useState([]);
  const [parentRowCount, setParentRowCount] = useState(0);
  const [expandedRows, setExpandedRows] = useState([]);
  const [popupTitle, setPopupTitle] = useState("Details");
  const [onStatusSelect, setOnStatusSelect] = useState(
    sessionStorage.getItem("selectedAppStatusText") || "In Review"
  );
  const [activeStatus, setActiveStatus] = useState(
    sessionStorage.getItem("selectedAppStatus") ||
      "PRODUCTION_APPROVAL_IN_REVIEW"
  );
  const [selectedTab, setSelectedTab] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedEcus, setSelectedEcus] = useState([]);
  const [sortModel, setSortModel] = useState([
    {
      field: "statusChangeDate",
      sort: "desc",
    },
  ]);
  const loaderStartTime = useRef(null);

  // Logout handler for error UI
  const handleLogout = async () => {
    const app_portal = "oem-admin";
    const clientId =
      sessionStorage.getItem("idpLogin") &&
      sessionStorage.getItem("idpLogin") === "true"
        ? getConfig().REACT_APP_VW_OEM_ADMIN_CLIENT_ID
        : getConfig().REACT_APP_OEM_ADMIN_CLIENT_ID;

    const scopeId =
      "rs%2FigniteStorePortfolioManager%20openid%20aws.cognito.signin.user.admin";
    const cognitoDomain = getConfig().REACT_APP_COGNITO_DOMAIN;
    const redirect_uri = getConfig().REACT_APP_REDIRECT_URI;
    if (
      sessionStorage.getItem("idpLogin") &&
      sessionStorage.getItem("idpLogin") === "true"
    ) {
      const revokeURL = `${getConfig().REACT_APP_COGNITO_DOMAIN}/oauth2/revoke`;
      try {
        const body = new URLSearchParams();
        body.set("token", sessionStorage.getItem("refresh_token"));
        body.set("client_id", clientId);

        const response = await fetch(revokeURL, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: body.toString(),
        });
        if (response.ok) {
          const redirect_uri = getConfig().REACT_APP_REDIRECT_URI;
          let logoutUrl = `${
            getConfig().REACT_APP_VW_LOGOUT_URL
          }${sessionStorage.getItem(
            "IDP_TOKEN"
          )}&post_logout_redirect_uri=${redirect_uri}&state=logout_state_1`;
          sessionStorage.clear();
          sessionStorage.setItem("idpLogout", true);
          globalThis.location.href = logoutUrl;
        }
      } catch (err) {
        console.error("Error revoking token:", err);
      }
    } else {
      const logoutUrl = `${cognitoDomain}/logout?client_id=${clientId}&response_type=code&scope=${scopeId}&redirect_uri=${redirect_uri}&portal=${app_portal}`;

      sessionStorage.clear();
      globalThis.location.href = logoutUrl;
    }
  };

  const handleInvalidPortal = useCallback(() => {
    setInvalidUserLogin(true);
    setInvalidUserLoginMessage(
      `${sessionStorage.getItem("customPortal")} user is trying to login in oem-admin portal`
    );
    sessionStorage.setItem("invalidUser", "true");
  }, []);

  const handleSelfApiResponse = useCallback(
    (json) => {
      if (
        json[0] !== undefined &&
        json[0].detailedErrorCode === "USER_AUTHENTICATION_EXCEPTION"
      ) {
        console.error("Authentication exception:", json[0].message);
        setInvalidUserLogin(true);
        setInvalidUserLoginMessage("Authentication exception occurred");
        setLoader(false);
        setAuthCheckComplete(true);
        return;
      }
      if (
        json.results !== undefined &&
        json?.messages?.[0]?.["key"] !== "success.key"
      ) {
        navigate(`/oem-admin/myprofile`);
        return;
      }
      if (
        json.results !== undefined &&
        json?.messages?.[0]?.["key"] === "success.key"
      ) {
        sessionStorage.setItem("userId", json.results[0]["id"]);
        sessionStorage.setItem(
          "setCorporateContactName",
          json.results[0]["corporateContactName"]
        );
        setAuthCheckComplete(true);
        sessionStorage.removeItem("invalidUser");
        sessionStorage.removeItem("pendingApproval");
      }
    },
    [navigate]
  );

  const handleSelfApiError = useCallback((error) => {
    const scopeError = "Scope Doesn't match With User Attribute Scope";
    const pendingError =
      "Your account creation is pending for approval. Please try after sometime";
    if (error?.message === scopeError) {
      setInvalidUserLogin(true);
      setInvalidUserLoginMessage(
        `${sessionStorage.getItem("customPortal")} user is trying to login in oem-admin portal`
      );
      sessionStorage.setItem("invalidUser", "true");
      setLoader(false);
    } else if (error?.message === pendingError) {
      setPendingApprovalUser(true);
      sessionStorage.setItem("pendingApproval", "true");
      setLoader(false);
    } else {
      console.error("General API error:", error?.message);
    }
    setAuthCheckComplete(true);
  }, []);

  // Self API call for domain validation and error handling
  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const json = await Promise.resolve(dispatch(fetchOemAdminSelf()));
        if (!isMounted) return;
        if (sessionStorage.getItem("customPortal") === "oem-admin") {
          handleSelfApiResponse(json);
        } else {
          handleInvalidPortal();
        }
      } catch (error) {
        if (!isMounted) return;
        handleSelfApiError(error);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [dispatch, handleInvalidPortal, handleSelfApiError, handleSelfApiResponse]);

  const [isMenuLoading, setIsMenuLoading] = useState(true); // Start immediately with loader
  const requestIdRef = useRef(0);
  const previousStatusRef = useRef(activeStatus);
  const isInitialMount = useRef(true);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const isPortfolioChanging = useRef(false);

  const handlePortfolioChange = useCallback(
    async (newPortfolioId) => {
      if (
        isPortfolioChanging.current &&
        newPortfolioId &&
        newPortfolioId !== "null"
      ) {
        isPortfolioChanging.current = false;
      }
      if (isPortfolioChanging.current) {
        return;
      }

      isPortfolioChanging.current = true;

      const currentRequestId = ++requestIdRef.current;

      try {
        setLoader(true);
        setIsMenuLoading(true);
        console.log(
          "🔄 Portfolio changed - refetching data for current status:",
          activeStatus
        );

        await Promise.resolve(dispatch(fetchContentsDetails(activeStatus, true)));

        await Promise.all([
          Promise.resolve(dispatch(fetchAdminCountriesDetails())),
          Promise.resolve(dispatch(fetchOemAdminEcuDetails())),
        ]);

        setRetryCount(0);
      } catch (error) {
        console.error(
          "Error handling portfolio change in ApplicationList:",
          error
        );

        if (currentRequestId === requestIdRef.current && retryCount < 3) {
          setRetryCount((prev) => prev + 1);

          isPortfolioChanging.current = false;

          setTimeout(() => {
            handlePortfolioChange(newPortfolioId);
          }, 1000 * (retryCount + 1));
        } else {
          isPortfolioChanging.current = false;
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          console.log(
            "Portfolio change API completed, letting data processing effect handle loader"
          );
          isPortfolioChanging.current = false; // Reset flag
        }
      }
    },
    [dispatch, activeStatus, isCountNeeded, retryCount]
  );

  usePortfolioChange(handlePortfolioChange);

  const getInitialOpenSections = () => {
    const savedStatus =
      sessionStorage.getItem("selectedAppStatus") ||
      "PRODUCTION_APPROVAL_IN_REVIEW";

    const statusSectionMap = {
      APPROVED_FOR_BETA: "Beta",
      STAGING_APPROVAL_IN_REVIEW: "Staging",
      REJECTED_AT_STAGING: "Staging",
      STAGING_APPROVAL_APPROVED: "Staging",
      DELIST_AT_STAGING: "Staging",
      FORCE_UNINSTALL_AT_STAGING: "Staging",

      PRODUCTION_APPROVAL_IN_REVIEW: "Production",
      REJECTED_AT_PRODUCTION: "Production",
      PRODUCTION_APPROVAL_APPROVED: "Production",
      DELIST_AT_PRODUCTION: "Production",
      FORCE_UNINSTALL_AT_PRODUCTION: "Production",
    };

    const sectionToOpen = statusSectionMap[savedStatus] || "Production";

    return {
      Beta: sectionToOpen === "Beta",
      Staging: sectionToOpen === "Staging",
      Production: sectionToOpen === "Production",
    };
  };

  const [openSections, setOpenSections] = useState(getInitialOpenSections());

  const handleExpand = (title) => {
    setExpandedRows((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    );
  };

  const clickRow = (rowData) => {
    navigate(
      `/ui/oem-admin/applications/${sessionStorage.getItem(
        "portfolioId"
      )}/content/${rowData.id}`
    );
  };

  const handleOpenPopup = (event, content, field = "") => {
    let processedContent;
    let title;

    if (field === "countries" && Array.isArray(content)) {
      const codeToName = (code) => {
        const match = countries.find((c) => c.iso3Code === code);
        return match ? match.name : code;
      };
      processedContent = content.map(codeToName);
      title = `Countries (${processedContent.length})`;
    } else if (Array.isArray(content)) {
      processedContent = content;
      title = `${field || "Items"} (${processedContent.length})`;
    } else if (typeof content === "string") {
      processedContent = [content];
      title = field
        ? field.charAt(0).toUpperCase() + field.slice(1)
        : "Details";
    } else {
      processedContent = ["N/A"];
      title = "Details";
    }

    setAnchorEl(event.currentTarget);
    setPopupContent(processedContent);
    setPopupTitle(title);
  };

  const handleClose = () => setAnchorEl(null);
  const toggleDrawer = (newOpen) => () => {
    setOpenFilter(newOpen);
  };
  const fetchappList = useSelector(
    (state) => state.contentAppList.contentDetails
  );
  const isContentLoading = useSelector((state) => state.contentAppList.loading);

  const countries = useSelector((state) => state.oemAdminCountires.countries);
  const memoizedCountries = useMemo(() => countries, [countries]);
  const ecuTypeData = useSelector((state) => state.oemAdminEcuTypes.ecu);
  const memoizedEcu = useMemo(() => ecuTypeData, [ecuTypeData]);
  const handleFilterApiCalls = (data) => {
    setSelectedEcus(data.selectedEcus);
    setSelectedCountries(data.selectedCountries);
  };

  const applyTabAndSearchFilters = (contents, tab, search, countries, ecus) => {
    let filtered = contents;
    if (tab === "OEM Apps") {
      filtered = filtered.filter((item) => item.isOemApp);
    } else if (tab === "3rd Party Apps") {
      filtered = filtered.filter((item) => !item.isOemApp);
    }
    if (search) {
      const lowerSearch = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.title?.toLowerCase().includes(lowerSearch) ||
          item.packageId?.toLowerCase().includes(lowerSearch)
      );
    }
    const countryCodes = countries.map((c) => c.iso3Code);
    if (countryCodes.length > 0) {
      filtered = filtered.filter((item) =>
        item.countries?.some((country) => countryCodes.includes(country))
      );
    }
    if (ecus.length > 0) {
      filtered = filtered.filter((item) =>
        item.ecuTypes?.some((ecu) => ecus.includes(ecu))
      );
    }
    return filtered;
  };

  const getServerCount = (appList, tab, status) => {
    if (tab === "OEM Apps") return appList?.groupAppsCount?.[status] || 0;
    if (tab === "3rd Party Apps") return appList?.thirdPartyAppsCount?.[status] || 0;
    return (
      (appList?.groupAppsCount?.[status] || 0) +
      (appList?.thirdPartyAppsCount?.[status] || 0)
    );
  };

  const getFieldValue = (item, field) => {
    if (field === "statusChangeDate") {
      return item[field] ? new Date(item[field]).getTime() : 0;
    }
    return item[field];
  };

  const sortParentRows = (rows, model) => {
    if (model.length === 0) return rows;
    const { field, sort } = model[0];
    return [...rows].sort((a, b) => {
      const valueA = getFieldValue(a, field);
      const valueB = getFieldValue(b, field);
      if (valueA < valueB) return sort === "asc" ? -1 : 1;
      if (valueA > valueB) return sort === "asc" ? 1 : -1;
      return 0;
    });
  };

  const buildDisplayRows = (parentRows, grouped, expanded) => {
    const rows = [];
    parentRows.forEach((parentRow) => {
      rows.push(parentRow);
      if (expanded.includes(parentRow.title) && parentRow.hasChildren) {
        const childRows = grouped[parentRow.title].slice(1).map((child) => ({
          ...child,
          hideTitle: true,
        }));
        rows.push(...childRows);
      }
    });
    return rows;
  };

  const handleLoaderVisibility = (loaderRef) => {
    if (loaderRef.current) {
      const elapsed = Date.now() - loaderRef.current;
      const remaining = Math.max(0, 500 - elapsed);
      loaderRef.current = null;
      return remaining;
    }
    return 0;
  };

  useEffect(() => {
    const filteredContent = applyTabAndSearchFilters(
      fetchappList?.portfolioContents || [],
      selectedTab,
      searchTerm,
      selectedCountries,
      selectedEcus
    );

    const grouped = {};
    filteredContent.forEach((item) => {
      if (!grouped[item.title]) grouped[item.title] = [];
      grouped[item.title].push(item);
    });

    const serverCount = getServerCount(fetchappList, selectedTab, activeStatus);
    let groupedKeys = Object.keys(grouped);
    if (groupedKeys.length > serverCount && serverCount > 0) {
      groupedKeys = groupedKeys.slice(0, serverCount);
    }

    const parentRows = sortParentRows(
      groupedKeys.map((title) => ({
        ...grouped[title][0],
        hasChildren: grouped[title].length > 1,
        hideTitle: false,
      })),
      sortModel
    );

    setFilteredRows(buildDisplayRows(parentRows, grouped, expandedRows));
    setParentRowCount(parentRows.length);

    if (fetchappList?.portfolioContents && authCheckComplete) {
      const hideLoader = () => {
        if (!isContentLoading) {
          setLoader(false);
          setIsMenuLoading(false);
          console.log("Data processed and ready for UI - turning off loader");
        }
      };

      // Ensure loader shows for at least 500ms to provide visual feedback
      const remaining = handleLoaderVisibility(loaderStartTime);
      if (remaining > 0) {
        console.log(`⏱️ Delaying loader hide by ${remaining}ms for better UX`);
        setTimeout(hideLoader, remaining);
      } else {
        hideLoader();
      }
    }
  }, [
    fetchappList,
    searchTerm,
    expandedRows,
    selectedTab,
    selectedCountries,
    selectedEcus,
    activeStatus,
    authCheckComplete,
    sortModel,
    isContentLoading,
  ]);

  const oemSections = {
    Beta: [
      {
        text: "Approved",
        status: "APPROVED_FOR_BETA",
        isCountNeeded: false,
        icon: (
          <svg
            width="18"
            height="16"
            viewBox="0 0 18 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.40962 16L4.962 13.5619L2.21914 12.9524L2.48581 10.1333L0.619141 8L2.48581 5.86667L2.21914 3.04762L4.962 2.4381L6.40962 0L9.00009 1.10476L11.5906 0L13.0382 2.4381L15.781 3.04762L15.5144 5.86667L17.381 8L15.5144 10.1333L15.781 12.9524L13.0382 13.5619L11.5906 16L9.00009 14.8952L6.40962 16ZM8.20009 10.7048L12.5049 6.4L11.4382 5.29524L8.20009 8.53333L6.562 6.93333L5.49533 8L8.20009 10.7048Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },
      {
        text: "Closed Beta - Approved",
        status: "CLOSED_BETA_BETA_APPROVED",
        isCountNeeded: false,
        icon: (
          <svg
            width="18"
            height="16"
            viewBox="0 0 18 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.40962 16L4.962 13.5619L2.21914 12.9524L2.48581 10.1333L0.619141 8L2.48581 5.86667L2.21914 3.04762L4.962 2.4381L6.40962 0L9.00009 1.10476L11.5906 0L13.0382 2.4381L15.781 3.04762L15.5144 5.86667L17.381 8L15.5144 10.1333L15.781 12.9524L13.0382 13.5619L11.5906 16L9.00009 14.8952L6.40962 16ZM8.20009 10.7048L12.5049 6.4L11.4382 5.29524L8.20009 8.53333L6.562 6.93333L5.49533 8L8.20009 10.7048Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      }
    ],
    Staging: [
      {
        text: "In Review",
        status: "STAGING_APPROVAL_IN_REVIEW",
        isCountNeeded: false,
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.19998 16C2.75998 16 2.38331 15.8433 2.06998 15.53C1.75664 15.2167 1.59998 14.84 1.59998 14.4V1.6C1.59998 1.16 1.75664 0.783333 2.06998 0.47C2.38331 0.156667 2.75998 0 3.19998 0H10.4L14.4 4.8V14.12L10.76 10.4C10.9066 10.1733 11.0166 9.92667 11.09 9.66C11.1633 9.39333 11.2 9.10667 11.2 8.8C11.2 7.92 10.8866 7.16667 10.26 6.54C9.63331 5.91333 8.87998 5.6 7.99998 5.6C7.11998 5.6 6.36664 5.91333 5.73998 6.54C5.11331 7.16667 4.79998 7.92 4.79998 8.8C4.79998 9.68 5.11331 10.4333 5.73998 11.06C6.36664 11.6867 7.11998 12 7.99998 12C8.30664 12 8.59331 11.9633 8.85998 11.89C9.12664 11.8167 9.37331 11.7067 9.59998 11.56L13.76 15.68C13.4533 15.8133 13.1333 15.9 12.8 15.94C12.4666 15.98 12.1333 16 11.8 16H3.19998ZM7.99998 10.4C7.55998 10.4 7.18331 10.2433 6.86998 9.93C6.55664 9.61667 6.39998 9.24 6.39998 8.8C6.39998 8.36 6.55664 7.98333 6.86998 7.67C7.18331 7.35667 7.55998 7.2 7.99998 7.2C8.43998 7.2 8.81664 7.35667 9.12998 7.67C9.44331 7.98333 9.59998 8.36 9.59998 8.8C9.59998 9.24 9.44331 9.61667 9.12998 9.93C8.81664 10.2433 8.43998 10.4 7.99998 10.4Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },
      {
        text: "Rejected",
        status: "REJECTED_AT_STAGING",
        isCountNeeded: false,
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.12 12L8 9.12L10.88 12L12 10.88L9.12 8L12 5.12L10.88 4L8 6.88L5.12 4L4 5.12L6.88 8L4 10.88L5.12 12ZM8 16C6.89333 16 5.85333 15.79 4.88 15.37C3.90667 14.95 3.06 14.38 2.34 13.66C1.62 12.94 1.05 12.0933 0.63 11.12C0.21 10.1467 0 9.10667 0 8C0 6.89333 0.21 5.85333 0.63 4.88C1.05 3.90667 1.62 3.06 2.34 2.34C3.06 1.62 3.90667 1.05 4.88 0.63C5.85333 0.21 6.89333 0 8 0C9.10667 0 10.1467 0.21 11.12 0.63C12.0933 1.05 12.94 1.62 13.66 2.34C14.38 3.06 14.95 3.90667 15.37 4.88C15.79 5.85333 16 6.89333 16 8C16 9.10667 15.79 10.1467 15.37 11.12C14.95 12.0933 14.38 12.94 13.66 13.66C12.94 14.38 12.0933 14.95 11.12 15.37C10.1467 15.79 9.10667 16 8 16Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },
      {
        text: "Approved",
        status: "STAGING_APPROVAL_APPROVED",
        isCountNeeded: false,
        icon: (
          <svg
            width="18"
            height="16"
            viewBox="0 0 18 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.40962 16L4.962 13.5619L2.21914 12.9524L2.48581 10.1333L0.619141 8L2.48581 5.86667L2.21914 3.04762L4.962 2.4381L6.40962 0L9.00009 1.10476L11.5906 0L13.0382 2.4381L15.781 3.04762L15.5144 5.86667L17.381 8L15.5144 10.1333L15.781 12.9524L13.0382 13.5619L11.5906 16L9.00009 14.8952L6.40962 16ZM8.20009 10.7048L12.5049 6.4L11.4382 5.29524L8.20009 8.53333L6.562 6.93333L5.49533 8L8.20009 10.7048Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },
      {
        text: "Delisted",
        status: "DELIST_AT_STAGING",
        isCountNeeded: false,
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 8C8 6.89697 8.39091 5.95455 9.17273 5.17273C9.95455 4.39091 10.897 4 12 4C13.103 4 14.0455 4.39091 14.8273 5.17273C15.6091 5.95455 16 6.89697 16 8H8ZM4 12C2.89697 12 1.95455 11.6091 1.17273 10.8273C0.390909 10.0455 0 9.10303 0 8H8C8 9.10303 7.60909 10.0455 6.82727 10.8273C6.04545 11.6091 5.10303 12 4 12ZM8 8C6.89697 8 5.95455 7.60909 5.17273 6.82727C4.39091 6.04545 4 5.10303 4 4C4 2.89697 4.39091 1.95455 5.17273 1.17273C5.95455 0.390909 6.89697 0 8 0V8ZM8 16V8C9.10303 8 10.0455 8.39091 10.8273 9.17273C11.6091 9.95455 12 10.897 12 12C12 13.103 11.6091 14.0455 10.8273 14.8273C10.0455 15.6091 9.10303 16 8 16Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },
      {
        text: "Force Uninstalled",
        status: "FORCE_UNINSTALL_AT_STAGING",
        isCountNeeded: false,
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 8C8 6.89697 8.39091 5.95455 9.17273 5.17273C9.95455 4.39091 10.897 4 12 4C13.103 4 14.0455 4.39091 14.8273 5.17273C15.6091 5.95455 16 6.89697 16 8H8ZM4 12C2.89697 12 1.95455 11.6091 1.17273 10.8273C0.390909 10.0455 0 9.10303 0 8H8C8 9.10303 7.60909 10.0455 6.82727 10.8273C6.04545 11.6091 5.10303 12 4 12ZM8 8C6.89697 8 5.95455 7.60909 5.17273 6.82727C4.39091 6.04545 4 5.10303 4 4C4 2.89697 4.39091 1.95455 5.17273 1.17273C5.95455 0.390909 6.89697 0 8 0V8ZM8 16V8C9.10303 8 10.0455 8.39091 10.8273 9.17273C11.6091 9.95455 12 10.897 12 12C12 13.103 11.6091 14.0455 10.8273 14.8273C10.0455 15.6091 9.10303 16 8 16Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },
      {
        text: "Closed Beta - Approved",
        status: "CLOSED_BETA_STAGING_APPROVED",
        isCountNeeded: false,
        icon: (
          <svg
            width="18"
            height="16"
            viewBox="0 0 18 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.40962 16L4.962 13.5619L2.21914 12.9524L2.48581 10.1333L0.619141 8L2.48581 5.86667L2.21914 3.04762L4.962 2.4381L6.40962 0L9.00009 1.10476L11.5906 0L13.0382 2.4381L15.781 3.04762L15.5144 5.86667L17.381 8L15.5144 10.1333L15.781 12.9524L13.0382 13.5619L11.5906 16L9.00009 14.8952L6.40962 16ZM8.20009 10.7048L12.5049 6.4L11.4382 5.29524L8.20009 8.53333L6.562 6.93333L5.49533 8L8.20009 10.7048Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      }
    ],
    Production: [
      {
        text: "In Review",
        status: "PRODUCTION_APPROVAL_IN_REVIEW",
        isCountNeeded: false,
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.19998 16C2.75998 16 2.38331 15.8433 2.06998 15.53C1.75664 15.2167 1.59998 14.84 1.59998 14.4V1.6C1.59998 1.16 1.75664 0.783333 2.06998 0.47C2.38331 0.156667 2.75998 0 3.19998 0H10.4L14.4 4.8V14.12L10.76 10.4C10.9066 10.1733 11.0166 9.92667 11.09 9.66C11.1633 9.39333 11.2 9.10667 11.2 8.8C11.2 7.92 10.8866 7.16667 10.26 6.54C9.63331 5.91333 8.87998 5.6 7.99998 5.6C7.11998 5.6 6.36664 5.91333 5.73998 6.54C5.11331 7.16667 4.79998 7.92 4.79998 8.8C4.79998 9.68 5.11331 10.4333 5.73998 11.06C6.36664 11.6867 7.11998 12 7.99998 12C8.30664 12 8.59331 11.9633 8.85998 11.89C9.12664 11.8167 9.37331 11.7067 9.59998 11.56L13.76 15.68C13.4533 15.8133 13.1333 15.9 12.8 15.94C12.4666 15.98 12.1333 16 11.8 16H3.19998ZM7.99998 10.4C7.55998 10.4 7.18331 10.2433 6.86998 9.93C6.55664 9.61667 6.39998 9.24 6.39998 8.8C6.39998 8.36 6.55664 7.98333 6.86998 7.67C7.18331 7.35667 7.55998 7.2 7.99998 7.2C8.43998 7.2 8.81664 7.35667 9.12998 7.67C9.44331 7.98333 9.59998 8.36 9.59998 8.8C9.59998 9.24 9.44331 9.61667 9.12998 9.93C8.81664 10.2433 8.43998 10.4 7.99998 10.4Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },
      {
        text: "Rejected",
        status: "REJECTED_AT_PRODUCTION",
        isCountNeeded: false,
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.12 12L8 9.12L10.88 12L12 10.88L9.12 8L12 5.12L10.88 4L8 6.88L5.12 4L4 5.12L6.88 8L4 10.88L5.12 12ZM8 16C6.89333 16 5.85333 15.79 4.88 15.37C3.90667 14.95 3.06 14.38 2.34 13.66C1.62 12.94 1.05 12.0933 0.63 11.12C0.21 10.1467 0 9.10667 0 8C0 6.89333 0.21 5.85333 0.63 4.88C1.05 3.90667 1.62 3.06 2.34 2.34C3.06 1.62 3.90667 1.05 4.88 0.63C5.85333 0.21 6.89333 0 8 0C9.10667 0 10.1467 0.21 11.12 0.63C12.0933 1.05 12.94 1.62 13.66 2.34C14.38 3.06 14.95 3.90667 15.37 4.88C15.79 5.85333 16 6.89333 16 8C16 9.10667 15.79 10.1467 15.37 11.12C14.95 12.0933 14.38 12.94 13.66 13.66C12.94 14.38 12.0933 14.95 11.12 15.37C10.1467 15.79 9.10667 16 8 16Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },
      {
        text: "Approved",
        status: "PRODUCTION_APPROVAL_APPROVED",
        isCountNeeded: false,
        icon: (
          <svg
            width="18"
            height="16"
            viewBox="0 0 18 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.40962 16L4.962 13.5619L2.21914 12.9524L2.48581 10.1333L0.619141 8L2.48581 5.86667L2.21914 3.04762L4.962 2.4381L6.40962 0L9.00009 1.10476L11.5906 0L13.0382 2.4381L15.781 3.04762L15.5144 5.86667L17.381 8L15.5144 10.1333L15.781 12.9524L13.0382 13.5619L11.5906 16L9.00009 14.8952L6.40962 16ZM8.20009 10.7048L12.5049 6.4L11.4382 5.29524L8.20009 8.53333L6.562 6.93333L5.49533 8L8.20009 10.7048Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },
      {
        text: "Delisted",
        status: "DELIST_AT_PRODUCTION",
        isCountNeeded: false,
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 8C8 6.89697 8.39091 5.95455 9.17273 5.17273C9.95455 4.39091 10.897 4 12 4C13.103 4 14.0455 4.39091 14.8273 5.17273C15.6091 5.95455 16 6.89697 16 8H8ZM4 12C2.89697 12 1.95455 11.6091 1.17273 10.8273C0.390909 10.0455 0 9.10303 0 8H8C8 9.10303 7.60909 10.0455 6.82727 10.8273C6.04545 11.6091 5.10303 12 4 12ZM8 8C6.89697 8 5.95455 7.60909 5.17273 6.82727C4.39091 6.04545 4 5.10303 4 4C4 2.89697 4.39091 1.95455 5.17273 1.17273C5.95455 0.390909 6.89697 0 8 0V8ZM8 16V8C9.10303 8 10.0455 8.39091 10.8273 9.17273C11.6091 9.95455 12 10.897 12 12C12 13.103 11.6091 14.0455 10.8273 14.8273C10.0455 15.6091 9.10303 16 8 16Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },
      {
        text: "Force Uninstalled",
        status: "FORCE_UNINSTALL_AT_PRODUCTION",
        isCountNeeded: false,
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 8C8 6.89697 8.39091 5.95455 9.17273 5.17273C9.95455 4.39091 10.897 4 12 4C13.103 4 14.0455 4.39091 14.8273 5.17273C15.6091 5.95455 16 6.89697 16 8H8ZM4 12C2.89697 12 1.95455 11.6091 1.17273 10.8273C0.390909 10.0455 0 9.10303 0 8H8C8 9.10303 7.60909 10.0455 6.82727 10.8273C6.04545 11.6091 5.10303 12 4 12ZM8 8C6.89697 8 5.95455 7.60909 5.17273 6.82727C4.39091 6.04545 4 5.10303 4 4C4 2.89697 4.39091 1.95455 5.17273 1.17273C5.95455 0.390909 6.89697 0 8 0V8ZM8 16V8C9.10303 8 10.0455 8.39091 10.8273 9.17273C11.6091 9.95455 12 10.897 12 12C12 13.103 11.6091 14.0455 10.8273 14.8273C10.0455 15.6091 9.10303 16 8 16Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },
      {
        text: "Closed Beta - Approved",
        status: "CLOSED_BETA_PRODUCTION_APPROVED",
        isCountNeeded: false,
        icon: (
          <svg
            width="18"
            height="16"
            viewBox="0 0 18 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.40962 16L4.962 13.5619L2.21914 12.9524L2.48581 10.1333L0.619141 8L2.48581 5.86667L2.21914 3.04762L4.962 2.4381L6.40962 0L9.00009 1.10476L11.5906 0L13.0382 2.4381L15.781 3.04762L15.5144 5.86667L17.381 8L15.5144 10.1333L15.781 12.9524L13.0382 13.5619L11.5906 16L9.00009 14.8952L6.40962 16ZM8.20009 10.7048L12.5049 6.4L11.4382 5.29524L8.20009 8.53333L6.562 6.93333L5.49533 8L8.20009 10.7048Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      }
    ],
  };

  const thirdPartySections = {
    Production: [
      {
        text: "In Review",
        status: "PRODUCTION_APPROVAL_IN_REVIEW",
        isCountNeeded: false,
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.19998 16C2.75998 16 2.38331 15.8433 2.06998 15.53C1.75664 15.2167 1.59998 14.84 1.59998 14.4V1.6C1.59998 1.16 1.75664 0.783333 2.06998 0.47C2.38331 0.156667 2.75998 0 3.19998 0H10.4L14.4 4.8V14.12L10.76 10.4C10.9066 10.1733 11.0166 9.92667 11.09 9.66C11.1633 9.39333 11.2 9.10667 11.2 8.8C11.2 7.92 10.8866 7.16667 10.26 6.54C9.63331 5.91333 8.87998 5.6 7.99998 5.6C7.11998 5.6 6.36664 5.91333 5.73998 6.54C5.11331 7.16667 4.79998 7.92 4.79998 8.8C4.79998 9.68 5.11331 10.4333 5.73998 11.06C6.36664 11.6867 7.11998 12 7.99998 12C8.30664 12 8.59331 11.9633 8.85998 11.89C9.12664 11.8167 9.37331 11.7067 9.59998 11.56L13.76 15.68C13.4533 15.8133 13.1333 15.9 12.8 15.94C12.4666 15.98 12.1333 16 11.8 16H3.19998ZM7.99998 10.4C7.55998 10.4 7.18331 10.2433 6.86998 9.93C6.55664 9.61667 6.39998 9.24 6.39998 8.8C6.39998 8.36 6.55664 7.98333 6.86998 7.67C7.18331 7.35667 7.55998 7.2 7.99998 7.2C8.43998 7.2 8.81664 7.35667 9.12998 7.67C9.44331 7.98333 9.59998 8.36 9.59998 8.8C9.59998 9.24 9.44331 9.61667 9.12998 9.93C8.81664 10.2433 8.43998 10.4 7.99998 10.4Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },

      {
        text: "Rejected",
        status: "REJECTED_AT_PRODUCTION",
        isCountNeeded: false,
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.12 12L8 9.12L10.88 12L12 10.88L9.12 8L12 5.12L10.88 4L8 6.88L5.12 4L4 5.12L6.88 8L4 10.88L5.12 12ZM8 16C6.89333 16 5.85333 15.79 4.88 15.37C3.90667 14.95 3.06 14.38 2.34 13.66C1.62 12.94 1.05 12.0933 0.63 11.12C0.21 10.1467 0 9.10667 0 8C0 6.89333 0.21 5.85333 0.63 4.88C1.05 3.90667 1.62 3.06 2.34 2.34C3.06 1.62 3.90667 1.05 4.88 0.63C5.85333 0.21 6.89333 0 8 0C9.10667 0 10.1467 0.21 11.12 0.63C12.0933 1.05 12.94 1.62 13.66 2.34C14.38 3.06 14.95 3.90667 15.37 4.88C15.79 5.85333 16 6.89333 16 8C16 9.10667 15.79 10.1467 15.37 11.12C14.95 12.0933 14.38 12.94 13.66 13.66C12.94 14.38 12.0933 14.95 11.12 15.37C10.1467 15.79 9.10667 16 8 16Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },
      {
        text: "Approved",
        status: "PRODUCTION_APPROVAL_APPROVED",
        isCountNeeded: false,
        icon: (
          <svg
            width="18"
            height="16"
            viewBox="0 0 18 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.40962 16L4.962 13.5619L2.21914 12.9524L2.48581 10.1333L0.619141 8L2.48581 5.86667L2.21914 3.04762L4.962 2.4381L6.40962 0L9.00009 1.10476L11.5906 0L13.0382 2.4381L15.781 3.04762L15.5144 5.86667L17.381 8L15.5144 10.1333L15.781 12.9524L13.0382 13.5619L11.5906 16L9.00009 14.8952L6.40962 16ZM8.20009 10.7048L12.5049 6.4L11.4382 5.29524L8.20009 8.53333L6.562 6.93333L5.49533 8L8.20009 10.7048Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },
      {
        text: "Delisted",
        status: "DELIST_AT_PRODUCTION",
        isCountNeeded: false,
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 8C8 6.89697 8.39091 5.95455 9.17273 5.17273C9.95455 4.39091 10.897 4 12 4C13.103 4 14.0455 4.39091 14.8273 5.17273C15.6091 5.95455 16 6.89697 16 8H8ZM4 12C2.89697 12 1.95455 11.6091 1.17273 10.8273C0.390909 10.0455 0 9.10303 0 8H8C8 9.10303 7.60909 10.0455 6.82727 10.8273C6.04545 11.6091 5.10303 12 4 12ZM8 8C6.89697 8 5.95455 7.60909 5.17273 6.82727C4.39091 6.04545 4 5.10303 4 4C4 2.89697 4.39091 1.95455 5.17273 1.17273C5.95455 0.390909 6.89697 0 8 0V8ZM8 16V8C9.10303 8 10.0455 8.39091 10.8273 9.17273C11.6091 9.95455 12 10.897 12 12C12 13.103 11.6091 14.0455 10.8273 14.8273C10.0455 15.6091 9.10303 16 8 16Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },
      {
        text: "Force Uninstalled",
        status: "FORCE_UNINSTALL_AT_PRODUCTION",
        isCountNeeded: false,
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 8C8 6.89697 8.39091 5.95455 9.17273 5.17273C9.95455 4.39091 10.897 4 12 4C13.103 4 14.0455 4.39091 14.8273 5.17273C15.6091 5.95455 16 6.89697 16 8H8ZM4 12C2.89697 12 1.95455 11.6091 1.17273 10.8273C0.390909 10.0455 0 9.10303 0 8H8C8 9.10303 7.60909 10.0455 6.82727 10.8273C6.04545 11.6091 5.10303 12 4 12ZM8 8C6.89697 8 5.95455 7.60909 5.17273 6.82727C4.39091 6.04545 4 5.10303 4 4C4 2.89697 4.39091 1.95455 5.17273 1.17273C5.95455 0.390909 6.89697 0 8 0V8ZM8 16V8C9.10303 8 10.0455 8.39091 10.8273 9.17273C11.6091 9.95455 12 10.897 12 12C12 13.103 11.6091 14.0455 10.8273 14.8273C10.0455 15.6091 9.10303 16 8 16Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      },
      {
        text: "Closed Beta - Approved",
        status: "CLOSED_BETA_PRODUCTION_APPROVED",
        isCountNeeded: false,
        icon: (
          <svg
            width="18"
            height="16"
            viewBox="0 0 18 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.40962 16L4.962 13.5619L2.21914 12.9524L2.48581 10.1333L0.619141 8L2.48581 5.86667L2.21914 3.04762L4.962 2.4381L6.40962 0L9.00009 1.10476L11.5906 0L13.0382 2.4381L15.781 3.04762L15.5144 5.86667L17.381 8L15.5144 10.1333L15.781 12.9524L13.0382 13.5619L11.5906 16L9.00009 14.8952L6.40962 16ZM8.20009 10.7048L12.5049 6.4L11.4382 5.29524L8.20009 8.53333L6.562 6.93333L5.49533 8L8.20009 10.7048Z"
              fill="#121415"
              fillOpacity="0.6"
            />
          </svg>
        ),
      }
    ],
  };
  const getSectionData = () => {
    let sectionsToUse;
    if (selectedTab === "OEM Apps") {
      sectionsToUse = oemSections;
    } else if (selectedTab === "3rd Party Apps") {
      sectionsToUse = thirdPartySections;
    } else {
      sectionsToUse = { ...oemSections, ...thirdPartySections };
    }
    return Object.entries(sectionsToUse).map(([label, items]) => {
      const updatedItems = items.map(
        ({ text, status, icon, isCountNeeded }) => {
          let count = 0;

          if (!selectedTab || selectedTab === null) {
            count =
              (fetchappList?.groupAppsCount?.[status] || 0) +
              (fetchappList?.thirdPartyAppsCount?.[status] || 0);
          } else if (selectedTab === "OEM Apps") {
            count = fetchappList?.groupAppsCount?.[status] || 0;
          } else if (selectedTab === "3rd Party Apps") {
            count = fetchappList?.thirdPartyAppsCount?.[status] || 0;
          }
          return {
            text,
            status,
            icon,
            isCountNeeded: isCountNeeded ?? false,
            count,
          };
        }
      );

      const totalCount = updatedItems.reduce(
        (sum, item) => sum + item.count,
        0
      );

      return {
        label,
        items: updatedItems,
        totalCount,
      };
    });
  };

  const getSectionForStatus = (status) => {
    const statusSectionMap = {
      APPROVED_FOR_BETA: "Beta",

      // Staging section statuses
      STAGING_APPROVAL_IN_REVIEW: "Staging",
      REJECTED_AT_STAGING: "Staging",
      STAGING_APPROVAL_APPROVED: "Staging",
      DELIST_AT_STAGING: "Staging",
      FORCE_UNINSTALL_AT_STAGING: "Staging",

      // Production section statuses
      PRODUCTION_APPROVAL_IN_REVIEW: "Production",
      REJECTED_AT_PRODUCTION: "Production",
      PRODUCTION_APPROVAL_APPROVED: "Production",
      DELIST_AT_PRODUCTION: "Production",
      FORCE_UNINSTALL_AT_PRODUCTION: "Production",
    };

    return statusSectionMap[status] || "Production";
  };

  const handleMenuItemClick = (item) => {
    console.log(
      "📱 Menu item clicked:",
      item.text,
      "status:",
      item.status,
      "current:",
      activeStatus
    );

    // Show loader immediately when menu item is clicked
    if (activeStatus !== item.status) {
      console.log(
        "🔄 Setting loader for menu change from",
        activeStatus,
        "to",
        item.status
      );
      loaderStartTime.current = Date.now();
      setIsMenuLoading(true);
      setLoader(true);
    }

    setActiveStatus(item.status);
    setOnStatusSelect(item.text);
    setIsCountNeeded(item.isCountNeeded ?? false);
    sessionStorage.setItem("selectedAppStatus", item.status);
    sessionStorage.setItem("selectedAppStatusText", item.text);

    const sectionToOpen = getSectionForStatus(item.status);
    if (sectionToOpen && !openSections[sectionToOpen]) {
      openSection(sectionToOpen);
    }
  };

  const openSection = (sectionName) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionName]: true,
    }));
  };
  const handleTabClick = (tabValue) => {
    if (selectedTab === tabValue) {
      setSelectedTab(null);
    } else {
      setSelectedTab(tabValue);

      // Always reset to default selection when switching tabs
      const defaultStatus = "PRODUCTION_APPROVAL_IN_REVIEW"; // Always default to Production In Review
      const defaultStatusText = "In Review";

      console.log(
        `🔄 Switching to ${tabValue}, resetting to default status: ${defaultStatus}`
      );

      // Update the states and session storage
      setActiveStatus(defaultStatus);
      setOnStatusSelect(defaultStatusText);
      sessionStorage.setItem("selectedAppStatus", defaultStatus);
      sessionStorage.setItem("selectedAppStatusText", defaultStatusText);

      // Ensure Production section is open for the default status
      openProductionSection();
    }
    setExpandedRows([]);
  };

  const openProductionSection = () => {
    setOpenSections((prev) => ({
      ...prev,
      Production: true,
    }));
  };

  const toggleSection = (label) => {
    setOpenSections((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };
  const sectionsToDisplay = getSectionData();

  const columns = [
    {
      field: "appIcon",
      headerName: "",
      width: 10,
      sortable: false,
      renderCell: (params) => {
        if (params.row.hideTitle) {
          return null;
        }

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <img
              src={params.row.icon}
              alt={params.row.title}
              width="30"
              height="30"
            />
          </Box>
        );
      },
    },
    {
      field: "title",
      headerName: "Application Name",
      flex: 1,
      minWidth: 170,
      maxWidth: 1000,
      renderHeader: () => (
        <Box sx={{ textAlign: "center" }}>Application Name</Box>
      ),
      renderCell: (params) => {
        if (params.row.hideTitle) return null;
        console.log("Rendering title for:", params.row.hideTitle);
        const columnWidth = params.colDef.computedWidth;
        const title = params.row.title || "Untitled";
        const packageId = params.row.packageId || "N/A";

        const shouldTruncateTitle =
          (columnWidth < 175 && title.length > 17) ||
          (columnWidth >= 175 && title.length > 25);

        const getTruncatedTitle = () => {
          if (columnWidth < 175) {
            return `${title.slice(0, 17)}...`;
          }
          return `${title.slice(0, 25)}...`;
        };

        const titleDisplay = shouldTruncateTitle ? getTruncatedTitle() : title;

        const shouldTruncatePackage =
          (columnWidth <= 170 && packageId.length > 23) ||
          (columnWidth > 170 && packageId.length > 29);

        const getTruncatedPackage = () => {
          if (columnWidth <= 170) {
            return `${packageId.slice(0, 23)}...`;
          }
          return `${packageId.slice(0, 29)}...`;
        };

        const packageDisplay = shouldTruncatePackage
          ? getTruncatedPackage()
          : packageId;

        return (
          <Box
            sx={{
              display: "flex",
              gap: "2px",
              flexDirection: "column",
            }}
          >
            <Tooltip
              title={shouldTruncateTitle ? title : ""}
              placement="top"
              arrow
              disableInteractive
              disableHoverListener={!shouldTruncateTitle}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  cursor: "pointer",
                  fontWeight: "600",
                  marginLeft: "5px",
                  color: "#2A2C2D",
                }}
              >
                {titleDisplay}
              </Typography>
            </Tooltip>

            <Tooltip
              title={shouldTruncatePackage ? packageId : ""}
              placement="top"
              arrow
              disableInteractive
              disableHoverListener={!shouldTruncatePackage}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#5A5B5C",
                  cursor: "pointer",
                  marginLeft: "5px",
                }}
              >
                {packageDisplay}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "contentVersion",
      headerName: "Version",
      sortable: false,
      maxWidth: 1000,
      minWidth: 70,
      flex: 1,
      renderCell: (params) => {
        const columnWidth = params.colDef.computedWidth;
        const fullVersion = params.value || "";
        const shouldTruncate =
          (columnWidth < 120 && fullVersion.length > 7) ||
          (columnWidth >= 120 && fullVersion.length > 15);

        const getTruncatedVersion = () => {
          if (columnWidth < 120) {
            return `${fullVersion.substring(0, 7)}...`;
          }
          return `${fullVersion.substring(0, 15)}...`;
        };

        const displayVersion = shouldTruncate ? getTruncatedVersion() : fullVersion;

        return (
          <div className="versionIcon">
            <Tooltip
              title={shouldTruncate ? fullVersion : ""}
              placement="top"
              arrow
              disableInteractive
              disableHoverListener={!shouldTruncate}
            >
              <Typography sx={{ marginLeft: "5px", fontSize: "14px" }}>
                {displayVersion}
              </Typography>
            </Tooltip>
            {params.row.hasChildren && (
              <div style={{ marginRight: "5px" }}>
                <svg
                  width="16"
                  height="17"
                  viewBox="0 0 16 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: "5px" }}
                >
                  <path
                    d="M9.96732 8.06366L8.76079 7.37995V6.01255L9.96732 5.32885L11.1739 6.01255V7.37995L9.96732 8.06366ZM9.96732 9.91368L12.7826 8.30497V5.08754L9.96732 3.47882L7.15207 5.08754V8.30497L9.96732 9.91368ZM8.51948 14.3376H13.0239C12.93 14.6862 12.7692 14.9677 12.5413 15.1822C12.3134 15.3967 12.0184 15.5308 11.6565 15.5844L2.88898 16.6502C2.44659 16.7172 2.04776 16.6133 1.6925 16.3385C1.33724 16.0637 1.1328 15.7051 1.07918 15.2627L0.0134059 6.47505C-0.0402179 6.03266 0.0670297 5.63718 0.335149 5.28863C0.603268 4.94007 0.958525 4.73898 1.40092 4.68536L2.32593 4.56471V6.17342L1.60201 6.27396L2.68789 15.0616L8.51948 14.3376ZM5.54336 12.7289C5.10096 12.7289 4.72225 12.5714 4.40721 12.2564C4.09217 11.9413 3.93465 11.5626 3.93465 11.1202V2.27229C3.93465 1.82989 4.09217 1.45117 4.40721 1.13613C4.72225 0.821094 5.10096 0.663574 5.54336 0.663574H14.3913C14.8337 0.663574 15.2124 0.821094 15.5274 1.13613C15.8425 1.45117 16 1.82989 16 2.27229V11.1202C16 11.5626 15.8425 11.9413 15.5274 12.2564C15.2124 12.5714 14.8337 12.7289 14.3913 12.7289H5.54336ZM5.54336 11.1202H14.3913V2.27229H5.54336V11.1202Z"
                    fill="#121415"
                    fillOpacity="0.6"
                  />
                </svg>
              </div>
            )}
          </div>
        );
      },
    },

    {
      field: "publisher",
      headerName: "Publisher",
      flex: 1,
      minWidth: 50,
      maxWidth: 1000,
      sortable: false,
      renderCell: (params) => {
        const name = params.row?.seller?.name || "N/A";
        const columnWidth = params.colDef.computedWidth;
        
        let displayName;
        if (columnWidth < 120 && name.length > 7) {
          displayName = `${name.substring(0, 7)}...`;
        } else if (columnWidth > 120 && name.length > 15) {
          displayName = `${name.substring(0, 15)}...`;
        } else {
          displayName = name;
        }
        
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={(event) => {
              event.stopPropagation();
              handleOpenPopup(event, name, "publisher");
            }}
          >
            <Typography
              sx={{
                textDecoration: "underline",
                fontSize: "14px",
                cursor: "pointer",
                marginLeft: "5px",
              }}
            >
              {displayName.replace(/^./, (str) => str.toUpperCase())}
            </Typography>
          </Box>
        );
      },
    },

    {
      field: "appStatus",
      headerName: "Status",
      flex: 1,
      minWidth: 160,
      maxWidth: 1000,
      sortable: false,
      renderCell: (params) => {
        const status = params.value;

        // Define status badge styles
        const getStatusBadge = (status) => {
          const statusConfig = {
            DRAFT: {
              className: styles.draftStatusBadge,
              text: "Draft",
            },
            PUBLISHED: {
              className: styles.publishedStatusBadge,
              text: "Published",
            },
            REJECTED_FOR_PRODUCTION: {
              className: styles.delistedStatusBadge,
              text: "Rejected for Production",
            },
            APPROVED_FOR_PRODUCTION: {
              className: styles.submittedStatusBadge,
              text: "Approved for Production",
            },
            APPROVED_FOR_BETA: {
              className: styles.submittedStatusBadge,
              text: "Approved for Beta",
            },
            APPROVED_FOR_STAGING: {
              className: styles.submittedStatusBadge,
              text: "Approved for Staging",
            },
            IN_REVIEW: {
              className: styles.inReviewStatusBadge,
              text: "In Review",
            },
            GENERATING_DELTA: {
              className: styles.inReviewStatusBadge,
              text: "Generating Delta",
            },
            DELISTED_BY_ADMIN: {
              className: styles.delistedStatusBadge,
              text: "Delisted by Admin",
            },
            DELISTED_BY_SELLER: {
              className: styles.delistedStatusBadge,
              text: "Delisted by Seller",
            },
            DELISTED_BY_APPROVER: {
              className: styles.delistedStatusBadge,
              text: "Delisted by Approver",
            },
            SUBMITTED_FOR_PRODUCTION: {
              className: styles.submittedStatusBadge,
              text: "Submitted for Production",
            },
            SUBMITTED_FOR_STAGING: {
              className: styles.submittedStatusBadge,
              text: "Submitted for Staging",
            },
            PRODUCTION_APPROVAL_IN_REVIEW: {
              className: styles.inReviewStatusBadge,
              text: "In Review",
            },
            REJECTED_AT_PRODUCTION: {
              className: styles.delistedStatusBadge,
              text: "Rejected",
            },
            PRODUCTION_APPROVAL_APPROVED: {
              className: styles.submittedStatusBadge,
              text: "Approved",
            },
            STAGING_APPROVAL_IN_REVIEW: {
              className: styles.inReviewStatusBadge,
              text: "In Review",
            },
            REJECTED_AT_STAGING: {
              className: styles.delistedStatusBadge,
              text: "Rejected",
            },
            STAGING_APPROVAL_APPROVED: {
              className: styles.submittedStatusBadge,
              text: "Approved",
            },
          };

          return (
            statusConfig[status] || {
              className: styles.defaultStatusBadge,
              text: status,
            }
          );
        };

        const statusBadge = getStatusBadge(status);
        const columnWidth = params.colDef.computedWidth;

        // Calculate maximum characters that can fit in the column
        const maxChars = Math.floor(columnWidth / 7); // Approximate 7px per character
        const shouldTruncate = statusBadge.text.length > maxChars;
        const displayText = shouldTruncate
          ? `${statusBadge.text.substring(0, maxChars - 3)}...`
          : statusBadge.text;

        return (
          <Box sx={{ marginLeft: "5px" }}>
            <Tooltip
              title={shouldTruncate ? statusBadge.text : ""}
              placement="top"
              arrow
              disableInteractive
            >
              <span className={statusBadge.className}>{displayText}</span>
            </Tooltip>
          </Box>
        );
      },
    },

    {
      field: "statusChangeDate",
      headerName: "Last Modified",
      flex: 1,
      minWidth: 130,
      maxWidth: 1000,
      sortable: true,
      type: "date",
      renderHeader: () => <Box sx={{ textAlign: "center" }}>Last Modified</Box>,
      valueGetter: (params) => {
        const timestamp = params;
        if (!timestamp) return null;
        const date = new Date(Number(timestamp));
        if (Number.isNaN(date)) return null;

        return date;
      },
      renderCell: (params) => {
        const timestamp = params.value;
        if (!timestamp) return "N/A";

        console.log("Timestamp value:", new Date(timestamp));

        const date = new Date(timestamp);
      console.log(params.row.hideTitle, "date", date);
        if (Number.isNaN(date)) return "Invalid";

        return (
          <Typography sx={{ fontSize: "14px", marginLeft: "5px" }}>
            {date.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </Typography>
        );
      },
    },

    {
      field: "isPublished",
      headerName: "Published",
      flex: 1,
      minWidth: 50,
      maxWidth: 1000,
      sortable: false,
      headerAlign: "center",
      renderHeader: () => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          Published
        </Box>
      ),
      renderCell: (params) => {
        const isPublished = params?.row?.appStatus === "PUBLISHED";
        const displayValue = isPublished ? "YES" : "NO";

        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>{displayValue}</Typography>
          </Box>
        );
      },
    },

    {
      field: "isPremiumApp",
      headerName: "App Pricing",
      flex: 1,
      maxWidth: 1000,
      sortable: false,
      renderHeader: () => <Box sx={{ textAlign: "center" }}>App Pricing</Box>,
      valueGetter: (params) => (params ? "Premium" : "Free"),
      renderCell: (params) => (
        <Typography sx={{ marginLeft: "5px", fontSize: "14px" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "expand",
      headerName: "",
      width: 10,
      sortable: false,
      renderCell: (params) =>
        params.row.hasChildren ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <IconButton
              sx={{ padding: "5px", marginTop: "-5px" }}
              onClick={(event) => {
                event.stopPropagation();
                handleExpand(params.row.title);
              }}
            >
              {expandedRows.includes(params.row.title) ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </IconButton>
          </Box>
        ) : null,
    },
  ];

  useEffect(() => {
    if (!authCheckComplete) {
      return;
    }
    const invalidUserFromSession =
      sessionStorage.getItem("invalidUser") === "true";
    const pendingApprovalFromSession =
      sessionStorage.getItem("pendingApproval") === "true";

    if (
      invalidUserLogin ||
      pendingApprovalUser ||
      invalidUserFromSession ||
      pendingApprovalFromSession
    ) {
      return;
    }

    const fetchStaticData = async () => {
      try {
        await Promise.all([
          Promise.resolve(dispatch(fetchAdminCountriesDetails())),
          Promise.resolve(dispatch(fetchOemAdminEcuDetails())),
        ]);
      } catch (error) {
        console.error("Error fetching static data:", error);
      }
    };

    fetchStaticData();
  }, [dispatch, invalidUserLogin, pendingApprovalUser, authCheckComplete]);

  useEffect(() => {
    // Wait for auth check to complete before proceeding
    if (!authCheckComplete) {
      return;
    }

    // Only block API calls for specific authentication failures
    const invalidUserFromSession =
      sessionStorage.getItem("invalidUser") === "true";
    const pendingApprovalFromSession =
      sessionStorage.getItem("pendingApproval") === "true";

    if (
      invalidUserLogin ||
      pendingApprovalUser ||
      invalidUserFromSession ||
      pendingApprovalFromSession
    ) {
      return;
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;

      const savedStatus = sessionStorage.getItem("selectedAppStatus");
      const statusToFetch = savedStatus || "PRODUCTION_APPROVAL_IN_REVIEW";

      if (savedStatus && activeStatus !== savedStatus) {
        setActiveStatus(savedStatus);
        return;
      }

      const fetchInitialContent = async () => {
        setIsMenuLoading(true);
        try {
          const result = dispatch(
            fetchContentsDetails(statusToFetch, isCountNeeded)
          );

          return result;
        } catch (error) {
          console.error("Error fetching initial content:", error);
          setLoader(false);
          setIsMenuLoading(false);
        }
      };

      fetchInitialContent();
      return;
    }

    // Prevent multiple calls for the same status
    if (previousStatusRef.current === activeStatus || !activeStatus) {
      console.log("Preventing duplicate call for same status:", activeStatus);
      return;
    }

    // Clear expanded rows when status changes
    setExpandedRows([]);

    const currentRequestId = ++requestIdRef.current;

    const fetchContentData = async () => {
      // Only proceed if this is the latest request
      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      console.log(
        "🚀 Starting content fetch with loader for status:",
        activeStatus
      );
      if (!loaderStartTime.current) {
        loaderStartTime.current = Date.now();
      }
      setLoader(true);
      setIsMenuLoading(true);

      try {
        // Only fetch content data when status changes
        const result = dispatch(
          fetchContentsDetails(activeStatus, isCountNeeded)
        );

        // Only update if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          previousStatusRef.current = activeStatus;
          console.log("✅ Content fetch completed for status:", activeStatus);
        } else {
          console.log("⚠️ Content fetch completed but request was superseded");
        }

        return result;
      } catch (error) {
        console.error("❌ Error fetching content data:", error);
        if (currentRequestId === requestIdRef.current) {
          if (isInitialMount.current || !initialDataLoaded) {
            setLoader(false);
            setInitialDataLoaded(true);
            isInitialMount.current = false;
          } else {
            setIsMenuLoading(false);
          }
          setLoader(false); // Always turn off loader on error
        }
      }
    };

    if (activeStatus) {
      fetchContentData();
    }
  }, [
    activeStatus,
    isCountNeeded,
    dispatch,
    invalidUserLogin,
    pendingApprovalUser,
    authCheckComplete,
  ]);

  useEffect(() => {
    return () => {
      requestIdRef.current = 0;
    };
  }, []);

  // Handle navigation state changes (when returning from approval/rejection)
  useEffect(() => {
    if (location.state?.refresh && authCheckComplete && !invalidUserLogin && !pendingApprovalUser) {
      console.log('🔄 Detected approval/rejection action, triggering refresh...');
      
      // Force a refresh by resetting previous status and incrementing request ID
      previousStatusRef.current = null;
      const currentRequestId = ++requestIdRef.current;
      
      const currentStatus = activeStatus || sessionStorage.getItem('selectedAppStatus') || 'PRODUCTION_APPROVAL_IN_REVIEW';
      
      // Show loader
      loaderStartTime.current = Date.now();
      setLoader(true);
      setIsMenuLoading(true);
      
      // Fetch fresh data
      dispatch(fetchContentsDetails(currentStatus, isCountNeeded))
        .then(() => {
          if (currentRequestId === requestIdRef.current) {
            console.log('✅ Data refreshed after approval/rejection');
            previousStatusRef.current = currentStatus;
          }
        })
        .catch((error) => {
          console.error('❌ Error refreshing data after approval:', error);
          if (currentRequestId === requestIdRef.current) {
            setLoader(false);
            setIsMenuLoading(false);
          }
        });
      
      // Clear the state to prevent unnecessary reloads
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname, authCheckComplete, invalidUserLogin, pendingApprovalUser, activeStatus, dispatch, isCountNeeded]);

  // to monitor Redux loading state and ensure loader synchronization
  useEffect(() => {
    console.log("Redux loading state changed:", isContentLoading);

    if (!isContentLoading && authCheckComplete) {
      // If Redux loading is false and we have data, ensure local loaders are also off after a short delay
      const timeoutId = setTimeout(() => {
        if (!isContentLoading && fetchappList?.portfolioContents) {
          console.log(
            "Redux loading completed - ensuring local loaders are synchronized"
          );
          setLoader(false);
          setIsMenuLoading(false);
        }
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [isContentLoading, authCheckComplete, fetchappList?.portfolioContents]);

  // Show the main UI immediately
  const shouldShowMainUI = !invalidUserLogin && !pendingApprovalUser;

  const shouldShowLoader =
    (!authCheckComplete || loader || isMenuLoading || isContentLoading) &&
    shouldShowMainUI;

  return (
    <>
      <Topheader />
      {(invalidUserLogin || pendingApprovalUser) && (
        <div
          className="noReadyLinkMarketPlace"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            maxWidth: "100vw",
            minHeight: "60vh",
            textAlign: "center",
            padding: "20px",
            overflowX: "hidden",
            boxSizing: "border-box",
          }}
        >
          <div style={{ marginBottom: "20px" }}>
            <svg
              width="300"
              height="25"
              viewBox="0 0 300 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.4269 19.7366L5.16686 14.7584H2.73925V19.7366H-7.62939e-06V5.09715H5.03973C8.32438 5.09715 10.3911 7.10239 10.3911 10.0098C10.3911 12.0396 9.38646 13.5446 7.67238 14.2745L11.3302 19.7325H8.4228L8.4269 19.7366ZM2.73925 12.3964H4.89211C6.54469 12.3964 7.65187 11.6869 7.65187 10.0139C7.65187 8.34079 6.54469 7.63137 4.89211 7.63137H2.73925V12.4005V12.3964ZM22.3323 17.2065V19.7366H13.2165V5.09715H22.1437V7.62727H15.9558V11.1416H20.6592V13.6102H15.9558V17.2065H22.3323ZM34.212 16.9153H27.3311L26.1788 19.7366H23.2714L29.4183 5.09715H32.1166L38.284 19.7366H35.3561L34.2038 16.9153H34.212ZM33.2894 14.6558L30.7797 8.46792L28.2496 14.6558H33.2894ZM52.3862 12.3554C52.3862 16.4971 49.1672 19.7366 44.9845 19.7366H39.9653V5.09715H44.9845C49.1672 5.09715 52.3862 8.33669 52.3862 12.3513V12.3554ZM49.6019 12.3554C49.6019 9.84575 47.9288 7.69288 44.9599 7.69288H42.7004V17.145H44.9599C47.9288 17.145 49.6019 14.9921 49.6019 12.3554ZM60.7721 13.7783V19.7366H58.0329V13.7783L52.4929 5.10125H55.5889L59.3943 11.2072L63.1998 5.10125H66.3163L60.7762 13.7783H60.7721Z"
                fill="#003865"
              />
              <path
                d="M115.358 12.2897C115.358 13.7086 114.206 14.8609 112.787 14.8609C111.368 14.8609 110.216 13.7086 110.216 12.2897C110.216 10.8709 111.368 9.7186 112.787 9.7186C114.206 9.7186 115.358 10.8709 115.358 12.2897ZM84.0164 10.8709C84.1394 8.42279 86.6286 7.14337 87.9285 7.14337C89.6467 7.14337 91.4797 8.24646 91.4797 11.3466V19.6095H94.2025V11.1046C94.2025 6.37245 91.5289 4.69937 89.2366 4.69937C86.4112 4.69937 84.205 6.97525 84.0164 7.57805V4.97002H81.2936V19.6095H84.0164V10.8709ZM69.9675 0.00408317V19.6136H72.6821V0.00408317H69.9675ZM75.6387 4.97411V19.6136H78.3534V4.97411H75.6387ZM75.6387 0.00408317V2.75564H78.3534V0.00408317H75.6387ZM103.003 11.5639L108.875 4.97002H105.594L99.7917 11.5803V-1.52588e-05H97.1591V19.6095H99.7917V15.1889L101.301 13.483L106.833 19.6095H110.052L103.003 11.5639ZM226.999 0.00408317V19.6136H229.714V0.00408317H226.999ZM178.693 11.5639L184.565 4.97002H181.285L175.482 11.5803V-1.52588e-05H172.85V19.6095H175.482V15.1889L176.991 13.483L182523 19.6095H185.742L178.693 11.5639ZM255.154 17.4033C252.956 17.4033 250.623 15.6195 250.623 12.3184C250.623 9.01739 252.961 7.17208 255.154 7.17208C256.93 7.17208 257.926 7.9102 258.689 8.77135L260.694 7.76668C259.456 5.76145 257.541 4.70347 255.13 4.70347C252.173 4.70347 248.007 7.06546 248.007 12.3225C248.007 17.5796 252.173 19.8801 255.13 19.8801C257.537 19.8801 259.468 18.8344 260.744 16.8456L258.697 15.8368C257.943 16.6816 256.959 17.4074 255.163 17.4074L255.154 17.4033ZM164.587 8.40639V4.97411H161.839V19.6136H164.587V13.1058C164.886 9.78422 167.211 7.46323 170.242 7.46323C170.377 7.46323 170.533 7.46323 170.701 7.47143V4.71577C170.524 4.70347 170.34 4.70347 170.127 4.70347C167.859 4.70347 165.206 6.04439 164.583 8.40639H164.587ZM204.519 0.963645H201.78V4.97002H199.725V7.23769H201.78V16.2715C201.78 18.8303 202.813 19.876 205.343 19.876C206.426 19.876 207.193 19.6382 207.644 19.4372V17.186C207.254 17.2885 206.832 17.3664 206.356 17.3664C205.138 17.3664 204.519 16.7144 204.519 15.4309V7.24179H207.644V4.97411H204.519V0.967743V0.963645ZM138.076 4.70347C137.309 4.70347 136.058 4.888 134.771 5.71634C133.524 6.52007 132.228 7.96761 131.999 8.24646H131.826C130.986 5.67943 129.009 4.70347 127.258 4.70347C124.42 4.70347 122.202 6.97935 122.017 7.58215V4.97411H119.282V19.6136H122.017V10.8791C122.14 8.43099 124.642 7.15158 125.946 7.15158C127.668 7.15158 129.509 8.25466 129.509 11.3548V19.6177H132.835V10.8832C132.958 8.43509 135.46 7.15568 136.764 7.15568C138.486 7.15568 140.327 8.25876 140.327 11.3589V19.6218H143.062V11.121C143.062 6.38885 140.376 4.71577 138.076 4.71577V4.70347ZM224.793 12.2938C224.793 17.2229 221.324 19.876 218.06 19.876C215.739 19.876 213.426 18.4613 212.983 17.6698V24.8337H210.268V4.97002H212.983V7.03266L213.02 6.95884C213.397 6.17971 215.698 4.69937 218.064 4.69937C219.696 4.69937 221.336 5.38418 222.57 6.56518C223.587 7.54114 224.797 9.32494 224.797 12.2938H224.793ZM222.164 12.2938C222.164 9.75962 220.582 7.19259 217.559 7.19259C215.242 7.19259 212.901 8.94358 212.901 12.2938C212.901 15.6441 215.247 17.3951 217.559 17.3951C220.582 17.3951 222.164 14.8281 222.164 12.2938ZM198.717 13.2247H187.719L187.838 13.7906C188.33 16.1485 190.064 17.555 192.471 17.555C194.267 17.555 195.198 17.1203 196.178 16.4847L197.601 17.961C196.301 19.265 194.624 19.876 192.365 19.876C190.56 19.876 188.768 19.1871 187.452 17.9897C186.394 17.0219 185.131 15.2545 185.131 12.3184C185.131 7.06546 189.215 4.69937 192.115 4.69937C193.595 4.69937 195.694 5.32677 197.146 7.09417C198.401 8.61962 198.946 10.7356 198.725 13.2247H198.717ZM196.408 11.3507L196.33 10.8135C195.949 8.23006 193.874 7.05316 192.11 7.05316C190.347 7.05316 188.444 8.20546 187.87 10.7807L187.743 11.3548H196.412L196.408 11.3507ZM275.576 13.2247H264.578L264.697 13.7906C265.189 16.1485 266.923 17.555 269.33 17.555C271.127 17.555 272.057 17.1203 273.037 16.4847L274.46 17.961C273.16 19.265 271.483 19.876 269.224 19.876C267.42 19.876 265.628 19.1871 264.311 17.9897C263.253 17.0219 261.99 15.2545 261.99 12.3184C261.99 7.06546 266.075 4.69937 268.974 4.69937C270.454 4.69937 272.554 5.32677 274.005 7.09417C275.26 8.61962 275.805 10.7356 275.584 13.2247H275.576ZM273.263 11.3507L273.185 10.8135C272.804 8.23006 270.729 7.05316 268.966 7.05316C267.202 7.05316 265.299 8.20546 264.725 10.7807L264.598 11.3548H273.267L273.263 11.3507ZM156.717 4.97411H159.428V19.6136H156.717V17.5509L156.681 17.6247C156.303 18.4039 154.003 19.8801 151.637 19.8801C148.373 19.8801 144.903 17.2229 144.903 12.2938C144.903 9.32494 146.113 7.54114 147.13 6.56518C148.364 5.38418 150.005 4.70347 151.637 4.70347C153.958 4.70347 156.275 6.1223 156.717 6.91374V4.97411ZM156.804 12.2897C156.804 8.93948 154.458 7.18438 152.141 7.18438C149.119 7.18438 147.536 9.75141 147.536 12.2897C147.536 14.8281 149.119 17.3951 152.141 17.3951C154.458 17.3951 156.804 15.64 156.804 12.2897ZM156.717 4.97411H159.428V19.6136H156.717V17.5509L156.681 17.6247C156.303 18.4039 154.003 19.8801 151.637 19.8801C148.373 19.8801 144.903 17.2229 144.903 12.2938C144.903 9.32494 146.113 7.54114 147.13 6.56518C148.364 5.38418 150.005 4.70347 151.637 4.70347C153.958 4.70347 156.275 6.1223 156.717 6.91374V4.97411ZM156.804 12.2897C156.804 8.93948 154.458 7.18438 152.141 7.18438C149.119 7.18438 147.536 9.75141 147.536 12.2897C147.536 14.8281 149.119 17.3951 152.141 17.3951C154.458 17.3951 156.804 15.64 156.804 12.2897ZM243.41 4.97411H246.121V19.6136H243.41V17.5509L243.373 17.6247C242.996 18.4039 240.695 19.8801 238.329 19.8801C235.065 19.8801 231.596 17.2229 231.596 12.2938C231.596 9.32494 232.806 7.54114 233.823 6.56518C235.057 5.38418 236.697 4.70347 238.329 4.70347C240.65 4.70347 242.967 6.1223 243.41 6.91374V4.97411ZM243.496 12.2897C243.496 8.93948 241.151 7.18438 238.834 7.18438C235.811 7.18438 234.229 9.75141 234.229 12.2897C234.229 14.8281 235.811 17.3951 238.834 17.3951C241.151 17.3951 243.496 15.64 243.496 12.2897Z"
                fill="#0082FF"
              />
              <path
                d="M297.15 17.0752C296.719 17.0752 296.309 17.1737 295.94 17.3459L294.169 15.5744V9.25933L295.94 7.48784C296.309 7.66007 296.715 7.75848 297.15 7.75848C298.725 7.75848 300 6.48317 300 4.90851C300 3.33384 298.725 2.05853 297.15 2.05853C295.575 2.05853 294.3 3.33384 294.3 4.90851C294.3 5.33908 294.398 5.74915 294.571 6.11821L292.799 7.88971H286.484L284.713 6.11821C284.885 5.74915 284.983 5.34318 284.983 4.90851C284.983 3.33384 283.708 2.05853 282.133 2.05853C280.559 2.05853 279.283 3.33384 279.283 4.90851C279.283 6.48317 280.559 7.75848 282.133 7.75848C282.564 7.75848 282.974 7.66007 283.343 7.48784L285.115 9.25933V15.5744L283.343 17.3459C282.974 17.1737 282.568 17.0752 282.133 17.0752C280.559 17.0752 279.283 18.3506 279.283 19.9252C279.283 21.4999 280.559 22.7752 282.133 22.7752C283.708 22.7752 284.983 21.4999 284.983 19.9252C284.983 19.4946 284.885 19.0846 284.713 18.7155L286.484 16.944H292.799L294.571 18.7155C294.398 19.0846 294.3 19.4905 294.3 19.9252C294.3 21.4999 295.575 22.7752 297.15 22.7752C298.725 22.7752 300 21.4999 300 19.9252C300 18.3506 298.725 17.0752 297.15 17.0752ZM287.054 9.82933H292.229V15.0044H287.054V9.82933Z"
                fill="url(#paint0_linear_7581_33336)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_7581_33336"
                  x1="279.279"
                  y1="12.421"
                  x2="300.004"
                  y2="12.421"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#0082FF" />
                  <stop offset="0.2" stopColor="#007DFC" />
                  <stop offset="0.42" stopColor="#006FF5" />
                  <stop offset="0.67" stopColor="#0057E9" />
                  <stop offset="0.92" stopColor="#0037D9" />
                  <stop offset="1" stopColor="#002CD4" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          <img
            className="noAppsImagecls"
            src={require("../../../assets/images/noapps.png")}
            alt="no apps img"
            style={{ marginBottom: "20px" }}
          />

          {invalidUserLogin && (
            <div className="noAppsWelcomeTextcls">401 Error</div>
          )}
          {invalidUserLogin && (
            <div className="unauthorizedLogin">Unauthorized Login</div>
          )}
          {invalidUserLogin && (
            <div className="inavlidUserLoginMessage">
              Invalid user trying to login to portal. {invalidUserLoginMessage}
            </div>
          )}
          {pendingApprovalUser && (
            <div className="inavlidUserLoginMessage">
              Your signup request is pending approval. Please contact the
              partnership team at MarketplacePartnerships@harman.comm for further
              assistance
            </div>
          )}
          <Button
            style={{
              background: "#121415F2",
              textTransform: "none",
              height: "36px",
              marginTop: "12px",
            }}
            variant="contained"
            onClick={handleLogout}
          >
            <LogoutIcon fontSize="small" /> &nbsp; Logout
          </Button>
        </div>
      )}

      {shouldShowLoader && (
        <Backdrop
          sx={(theme) => ({
            color: "#fff",
            zIndex: theme.zIndex.drawer + 999,
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          })}
          open={shouldShowLoader}
        >
          <CircularProgress color="inherit" size={60} />
        </Backdrop>
      )}

      {shouldShowMainUI && !shouldShowLoader && (
        <>
          <Toolbar />
          <Box
            sx={{
              display: "flex",
            }}
          >
            <Box
              sx={{
                width: "225px",
                flexShrink: 0,
                paddingLeft: "75px",
                "@media (max-width: 1300px)": {
                  paddingLeft: "20px",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  marginTop: "17px",
                  height: "32px",
                }}
              >
                <Box
                  onClick={() => handleTabClick("OEM Apps")}
                  sx={{
                    minHeight: "28px",
                    height: "28px",
                    fontSize: "13px",
                    fontWeight: 500,
                    textTransform: "none",
                    px: 1.5,
                    backgroundColor:
                      selectedTab === "OEM Apps" ? "#0476E2" : "#FFFFFF",
                    color: selectedTab === "OEM Apps" ? "#FFFFFF" : "#0476E2",
                    "&:hover": {
                      backgroundColor:
                        selectedTab === "OEM Apps" ? "#0476E2" : "#f5f5f5",
                    },
                    border: "1px solid #0476E2",
                    borderRight: "none",
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: 1,
                    userSelect: "none",
                  }}
                >
                  OEM Apps
                </Box>
                <Box
                  onClick={() => handleTabClick("3rd Party Apps")}
                  sx={{
                    minHeight: "28px",
                    height: "28px",
                    fontSize: "13px",
                    fontWeight: 500,
                    textTransform: "none",
                    px: 1.5,
                    backgroundColor:
                      selectedTab === "3rd Party Apps" ? "#0476E2" : "#FFFFFF",
                    color:
                      selectedTab === "3rd Party Apps" ? "#FFFFFF" : "#0476E2",
                    "&:hover": {
                      backgroundColor:
                        selectedTab === "3rd Party Apps"
                          ? "#0476E2"
                          : "#f5f5f5",
                    },
                    border: "1px solid #0476E2",

                    borderTopRightRadius: "4px",
                    borderBottomRightRadius: "4px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: 1,
                    userSelect: "none",
                  }}
                >
                  3rd Party Apps
                </Box>
              </Box>

              {sectionsToDisplay.map((section) => (
                <Box key={section.label} sx={{ marginTop: "8px" }}>
                  <ListItem
                    button
                    onClick={() => toggleSection(section.label)}
                    sx={{
                      paddingLeft: "0px !important",
                      cursor: "pointer",
                      paddingTop: "4px",
                      paddingBottom: "4px",
                      borderRadius: "4px",
                    }}
                  >
                    {openSections[section.label] ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                    <ListItemText
                      sx={{ fontWeight: "600", paddingLeft: "10px" }}
                      primary={`${section.label} (${section.totalCount})`}
                    />
                  </ListItem>
                  <Collapse
                    in={openSections[section.label]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List>
                      {section.items.map((item) => (
                        <ListItem
                          key={item.status}
                          button
                          selected={activeStatus === item.status}
                          onClick={() => handleMenuItemClick(item)}
                          sx={{
                            borderRadius: "4px",
                            bgcolor:
                              activeStatus === item.status
                                ? "#E5F3FF"
                                : "#ffffff",
                            padding: "2px",
                            cursor: "pointer",
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                fontSize="11px"
                                lineHeight="20px"
                                paddingLeft="16px !important"
                                fontWeight={
                                  activeStatus === item.status
                                    ? "600"
                                    : "normal"
                                }
                                color={
                                  activeStatus === item.status
                                    ? "#0476E2"
                                    : "#2A2C2D"
                                }
                                display="grid"
                                gridTemplateColumns="1fr auto"
                                alignItems="center"
                              >
                                <span
                                  style={{
                                    maxWidth: "200px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "3px",
                                  }}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      "& svg path": {
                                        fill:
                                          activeStatus === item.status
                                            ? "#0476E2"
                                            : "#2A2C2D",
                                        transition: "fill 0.2s ease",
                                      },
                                    }}
                                  >
                                    {item.icon}
                                  </Box>
                                  {item.text}
                                </span>
                                <span
                                  style={{
                                    display: "flex",
                                    minWidth: "25px",
                                    maxWidth: "max-content",
                                  }}
                                >
                                  {item.count}
                                </span>
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              ))}
            </Box>
            <Box sx={{ flexGrow: 1, ml: 2, marginTop: "16px" }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                width="97%"
              >
                <Typography sx={{ fontSize: "20px" }}>
                  Applications -{" "}
                  <span
                    style={{
                      fontSize: "14px",
                      textTransform: "none",
                      color: "#5A5B5C",
                    }}
                  >
                    {onStatusSelect || "None"}
                  </span>
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search for application name or package name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ width: 351 }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <FilterAltOutlinedIcon
                    color="action"
                    className="filterIconCls"
                    onClick={toggleDrawer(true)}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  width: "97%",
                }}
              >
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  getRowId={(row) => row.id}
                  onRowClick={(row) => clickRow(row)}
                  disableColumnMenu
                  sortingMode="server"
                  onSortModelChange={(model) => setSortModel(model)}
                  sortModel={sortModel}
                  rowCount={parentRowCount}
                  localeText={{
                    MuiTablePagination: {
                      labelRowsPerPage: "Items per page:",
                    },
                  }}
                  components={{
                    NoRowsOverlay,
                  }}
                  sx={{
                    height: "auto",
                    minWidth: "100%",
                    "& .MuiDataGrid-columnHeader": {
                      fontSize: "14px",
                      fontWeight: "600",
                      backgroundColor: "#f0f0f0 !important",
                      color: "#121415",
                      lineHeight: "30px",
                      padding: "0px 0px 0px 4px",
                      display: "flex",
                      alignItems: "center",
                      marginLeft: "2px",
                    },

                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontSize: "14px",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "30px",
                      lineHeight: "30px",
                      padding: "0px",
                    },
                    "& .MuiDataGrid-cell": {
                      borderRight: "1px solid rgba(224, 224, 224, 1)",
                      color: "#5A5B5C",
                      fontWeight: "400",
                      lineHeight: "20px",
                      letterSpacing: "0.25px",
                      display: "flex",
                      alignItems: "center",
                      padding: "0px !important",
                    },
                    "& .MuiDataGrid-cell:last-child": { borderRight: "none" },
                    "& .MuiDataGrid-columnSeparator": { display: "none" },
                    "& .MuiDataGrid-container--top [role=row]": {
                      background: "#f0f0f0 !important",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      marginTop: "0px !important",
                    },
                    "& .MuiDataGrid-virtualScrollerContent": {
                      overflowX: "hidden !important",
                    },
                    "& .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon": {
                      opacity: 1,
                      visibility: "visible",
                    },
                  }}
                />
              </Box>

              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                slotProps={{
                  paper: {
                    sx: {
                      maxWidth: 300,
                      maxHeight: 300,
                      overflowY: "auto",
                      padding: 2,
                    },
                  },
                }}
              >
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {popupTitle}
                </Typography>
                {popupContent?.length > 0 ? (
                  popupContent.map((country) => (
                    <Typography key={country} sx={{ fontSize: "14px", mb: 0.5 }}>
                      {country}
                    </Typography>
                  ))
                ) : (
                  <Typography sx={{ fontSize: "14px" }}>N/A</Typography>
                )}
              </Popover>
            </Box>
            <DrawerFilter
              openFilter={openFilter}
              toggleDrawer={toggleDrawer}
              selectedCountries={memoizedCountries}
              selectedEcus={memoizedEcu}
              handleFilterApiCalls={handleFilterApiCalls}
            />
          </Box>
          <div>&nbsp;</div>
        </>
      )}
    </>
  );
}
